import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabaseClient, fetchFromSupabase } from '../config/supabase';
import { monitoringService } from '../services/monitoringService';
import { loggingService } from '../services/loggingService';
import { emailService } from '../services/emailService';
import { stripeService } from '../services/stripeService';
import toast from 'react-hot-toast';
import { useAuthStore } from './authStore';

export type SubscriptionPlan = 'free' | 'premium' | 'enterprise';

export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'trialing' | 'unpaid' | 'incomplete' | 'incomplete_expired';

export interface SubscriptionState {
  plan: SubscriptionPlan;
  status: SubscriptionStatus | null;
  subscriptionId: string | null;
  quizGenerationsLeft: number;
  currentBillingPeriod: {
    start: string | null;
    end: string | null;
  };
  resetsLeft: number;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  discount: number;
  analysisGenerated: number;
  setPlan: (plan: SubscriptionPlan) => void;
  setSubscriptionId: (id: string | null) => void;
  setQuizGenerationsLeft: (count: number) => void;
  decrementQuizGenerations: () => void;
  resetQuizGenerations: () => void;
  decrementResets: () => void;
  resetMonthlyLimits: () => void;
  fetchUserSubscription: (userId: string) => Promise<boolean>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setInitialized: (initialized: boolean) => void;
  setBillingPeriod: (start: string | null, end: string | null) => void;
  setStatus: (status: SubscriptionStatus | null) => void;
  checkSubscriptionStatus: () => Promise<void>;
  updateStripeDetails: (customerId: string, subscriptionId: string) => void;
  handleSubscriptionWebhook: (event: any) => Promise<void>;
  checkPaymentReminders: () => Promise<void>;
  getStripeSession: (priceId: string, discountPercentage?: number) => Promise<string>;
  handleCheckoutSuccess: (sessionId: string) => Promise<void>;
  createCustomerPortal: (userId: string) => Promise<string>;
  cancelSubscription: (userId: string) => Promise<void>;
  fetchSubscriptionStatus: (userId: string) => Promise<void>;
  setAnalysisGenerated: (count: number) => void;
}

export const useSubscriptionStore = create<SubscriptionState>()(
  persist(
    (set, get) => {
      // Inicjalizacja subskrypcji z localStorage, jeśli istnieje
      const savedPlan = localStorage.getItem('subscription_plan') as SubscriptionPlan;
      const savedGenerationsLeft = localStorage.getItem('quiz_generations_left');
      const savedStatus = localStorage.getItem('subscription_status') as SubscriptionStatus;
      
      // Define all methods first
      const setPlan = (plan: SubscriptionPlan) => {
        set({ plan });
        localStorage.setItem('subscription_plan', plan);
      };
      const setSubscriptionId = (id: string | null) => {
        set({ subscriptionId: id });
        if (id) {
          localStorage.setItem('subscription_id', id);
        } else {
          localStorage.removeItem('subscription_id');
        }
      };
       const setQuizGenerationsLeft = (count: number) => {
          const newCount = Math.max(0, count);
          set({ quizGenerationsLeft: newCount });
          localStorage.setItem('quiz_generations_left', newCount.toString());
        };
        const setLoading = (loading: boolean) => set({ loading });
        const setError = (error: string | null) => set({ error });
        const setInitialized = (initialized: boolean) => set({ initialized });
        const setStatus = (status: SubscriptionStatus | null) => {
             set({ status });
             if (status) {
                 localStorage.setItem('subscription_status', status);
             } else {
                 localStorage.removeItem('subscription_status');
             }
        };
        const setBillingPeriod = (start: string | null, end: string | null) => {
            set({ currentBillingPeriod: { start, end } });
            if (start) localStorage.setItem('billing_period_start', start); else localStorage.removeItem('billing_period_start');
            if (end) localStorage.setItem('billing_period_end', end); else localStorage.removeItem('billing_period_end');
        };
        const updateStripeDetails = (customerId: string, subscriptionId: string) => {
            set({ stripeCustomerId: customerId, stripeSubscriptionId: subscriptionId });
        };
        const setAnalysisGenerated = (count: number) => {
            set({ analysisGenerated: Math.max(0, count) });
        };
        const decrementQuizGenerations = () => {
          const current = get().quizGenerationsLeft;
          const newCount = Math.max(0, current - 1);
          set({ quizGenerationsLeft: newCount });
          localStorage.setItem('quiz_generations_left', newCount.toString());
        };
        const resetQuizGenerations = () => {
          const { plan } = get();
          let newCount = 1;
          if (plan === 'premium') newCount = 5;
          else if (plan === 'enterprise') newCount = 10;
          set({ quizGenerationsLeft: newCount });
          localStorage.setItem('quiz_generations_left', newCount.toString());
        };
        const decrementResets = () =>
        set((state) => ({ 
          resetsLeft: Math.max(0, state.resetsLeft - 1) 
          }));
        const resetMonthlyLimits = () =>
        set((state) => ({
          resetsLeft: state.plan === 'premium' ? 5 : 0,
          quizGenerationsLeft: state.plan === 'premium' ? 5 : 1
          }));

       const fetchUserSubscription = async (userId: string): Promise<boolean> => {
          try {
            set({ loading: true, error: null });
            const { user } = useAuthStore.getState();
            if (!user) {
              console.warn('[SubscriptionStore] User not found in authStore during fetch.');
              set({
                plan: 'free', status: null, quizGenerationsLeft: 1, resetsLeft: 0,
                stripeCustomerId: null, stripeSubscriptionId: null, loading: false,
                initialized: true, error: null
              });
              localStorage.setItem('subscription_plan', 'free');
              localStorage.setItem('quiz_generations_left', '1');
              localStorage.removeItem('subscription_status');
              return false;
            }
            const currentUserId = user.id;
             if (userId !== currentUserId) {
                console.warn(`[SubscriptionStore] Mismatch between passed userId (${userId}) and authStore userId (${currentUserId}). Using authStore userId.`);
                userId = currentUserId;
            }

            console.log(`[SubscriptionStore] Fetching ALL subscriptions for user: ${userId}`);

            const { data: subscriptions, error } = await fetchFromSupabase<any>({
              tableName: 'subscriptions',
              columns: '*',
              filters: { user_id: userId },
            });

            const isSupabaseError = (err: any): err is { code?: string; message?: string } => {
              return typeof err === 'object' && err !== null && ('message' in err || 'code' in err);
            };

            if (error) {
              if (isSupabaseError(error) && (error.code === 'PGRST116' || error.message?.includes('rows returned'))) {
                  console.log(`[SubscriptionStore] No subscriptions found for user ${userId}. Setting free plan.`);
          set({
                      plan: 'free', status: null, quizGenerationsLeft: 1, resetsLeft: 0,
                      stripeCustomerId: null, stripeSubscriptionId: null, loading: false,
                      initialized: true, error: null
                  });
                  localStorage.setItem('subscription_plan', 'free');
                  localStorage.setItem('quiz_generations_left', '1');
                  localStorage.removeItem('subscription_status');
                  return true;
              } else {
                  console.error('[SubscriptionStore] Error fetching subscriptions:', error);
          set({
                      loading: false, error: 'Nie udało się pobrać danych subskrypcji', initialized: true,
                      plan: 'free', status: null, quizGenerationsLeft: 1
                  });
                  localStorage.setItem('subscription_plan', 'free');
                  localStorage.setItem('quiz_generations_left', '1');
                  localStorage.removeItem('subscription_status');
                  return false;
              }
            }

            console.log(`[SubscriptionStore] Fetched ${subscriptions?.length ?? 0} subscription records for user ${userId}.`);
            const activeSubscription = subscriptions?.find((sub: any) => sub.status === 'active');

            if (activeSubscription) {
              console.log('[SubscriptionStore] Active subscription found:', activeSubscription);
              const planId = activeSubscription.plan_id || activeSubscription.price_id;
              console.log(`[SubscriptionStore] Checking planId: ${planId}`); 

              let determinedPlan: SubscriptionPlan = 'free';
              let generations = 1;
              let resets = 0;
              let determinedStatus: SubscriptionStatus = 'active';

              const premiumKeywords = ['premium', 'price_premium', 'price_1RGhNzEdLqZqOO1eOGAq48Qd', 'price_1RGhPVEdLqZqOO1eunyQwmtW'];
              const enterpriseKeywords = ['enterprise', 'price_enterprise'];

              if (planId && premiumKeywords.some(keyword => String(planId).includes(keyword))) {
                  determinedPlan = 'premium';
                  generations = typeof activeSubscription.quiz_generations_left === 'number' ? activeSubscription.quiz_generations_left : 5;
                  resets = 5;
              } else if (planId && enterpriseKeywords.some(keyword => String(planId).includes(keyword))) {
                  determinedPlan = 'enterprise';
                  generations = typeof activeSubscription.quiz_generations_left === 'number' ? activeSubscription.quiz_generations_left : 10;
                  resets = 10;
              } else {
                  if(planId) {
                      console.warn(`[SubscriptionStore] Unknown planId detected: ${planId}. Defaulting to free.`);
                  }
              }
               generations = Math.max(0, generations);

               const validStatuses: SubscriptionStatus[] = ['active', 'past_due', 'canceled', 'trialing', 'unpaid', 'incomplete', 'incomplete_expired'];
               if (!validStatuses.includes(activeSubscription.status)) {
                   console.warn(`[SubscriptionStore] Invalid status '${activeSubscription.status}' received. Defaulting to 'active'.`);
                   determinedStatus = 'active';
               } else {
                   determinedStatus = activeSubscription.status as SubscriptionStatus;
               }

              console.log(`[SubscriptionStore] Setting plan to: ${determinedPlan}, Status: ${determinedStatus}, Generations: ${generations}`);

              // ZMIANA: Bardziej odporne parsowanie WSZYSTKICH potencjalnych dat
              let billingStartISO: string | null = null;
              let billingEndISO: string | null = null;
              let createdAtISO: string | null = null; // Dodano
              let updatedAtISO: string | null = null; // Dodano

              const parseDateSafely = (dateValue: any): string | null => {
                  if (!dateValue) return null;
                  try {
                      // Najpierw spróbuj jako string ISO (bo tak przychodzi z Supabase)
                      if (typeof dateValue === 'string') {
                          const parsedDate = new Date(dateValue);
                          if (!isNaN(parsedDate.getTime())) {
                              return parsedDate.toISOString();
                          }
                      }
                      // Potem spróbuj jako timestamp (liczba sekund)
                      const timestamp = Number(dateValue);
                      if (!isNaN(timestamp)) {
                          return new Date(timestamp * 1000).toISOString();
                      }
                      console.warn(`[SubscriptionStore] Could not parse date value: ${dateValue}`);
                      return null; // Zwróć null, jeśli parsowanie się nie uda
                  } catch (e) {
                      console.error(`[SubscriptionStore] Error parsing date value ${dateValue}:`, e);
                      return null;
                  }
              };

              billingStartISO = parseDateSafely(activeSubscription.current_period_start);
              billingEndISO = parseDateSafely(activeSubscription.current_period_end);
              createdAtISO = parseDateSafely(activeSubscription.created_at); // Parsuj created_at
              updatedAtISO = parseDateSafely(activeSubscription.updated_at); // Parsuj updated_at

              console.log(`[SubscriptionStore] Processed Dates: Start=${billingStartISO}, End=${billingEndISO}, Created=${createdAtISO}, Updated=${updatedAtISO}`);

              // Użyj sparsowanych (lub null) wartości w set
        set({ 
                plan: determinedPlan, status: determinedStatus,
                stripeCustomerId: activeSubscription.customer_id || activeSubscription.stripe_customer_id,
                stripeSubscriptionId: activeSubscription.id || activeSubscription.stripe_subscription_id,
                quizGenerationsLeft: generations, resetsLeft: resets,
                currentBillingPeriod: {
                    start: billingStartISO,
                    end: billingEndISO,
                },
                // Można też zapisać sparsowane created/updated, jeśli są potrzebne
                loading: false, initialized: true, error: null
              });

              // Zaktualizuj localStorage używając sparsowanych wartości
               localStorage.setItem('subscription_plan', determinedPlan);
               localStorage.setItem('quiz_generations_left', generations.toString());
              if (determinedStatus) localStorage.setItem('subscription_status', determinedStatus); else localStorage.removeItem('subscription_status');
              if (billingStartISO) localStorage.setItem('billing_period_start', billingStartISO); else localStorage.removeItem('billing_period_start');
              if (billingEndISO) localStorage.setItem('billing_period_end', billingEndISO); else localStorage.removeItem('billing_period_end');

            } else {
              console.log(`[SubscriptionStore] No ACTIVE subscription found for user ${userId}. Setting free plan.`);
              set({
                plan: 'free', status: null, quizGenerationsLeft: 1, resetsLeft: 0,
                stripeCustomerId: subscriptions?.[0]?.customer_id || subscriptions?.[0]?.stripe_customer_id || null,
                stripeSubscriptionId: null, currentBillingPeriod: { start: null, end: null },
                loading: false, initialized: true, error: null
              });
               localStorage.setItem('subscription_plan', 'free');
               localStorage.setItem('quiz_generations_left', '1');
               localStorage.removeItem('subscription_status');
               localStorage.removeItem('billing_period_start');
               localStorage.removeItem('billing_period_end');
            }
            return true;
          } catch (error: any) {
            console.error('[SubscriptionStore] Unexpected error in fetchUserSubscription:', error);
            set({
              loading: false, error: 'Wystąpił nieoczekiwany błąd podczas pobierania danych subskrypcji',
              initialized: true, plan: 'free', status: null, quizGenerationsLeft: 1
            });
             localStorage.setItem('subscription_plan', 'free');
             localStorage.setItem('quiz_generations_left', '1');
             localStorage.removeItem('subscription_status');
            return false;
          }
        };
        const fetchSubscriptionStatus = async (userId: string) => {
          console.log('[SubscriptionStore] fetchSubscriptionStatus called, delegating to fetchUserSubscription.');
          await fetchUserSubscription(userId); // Call the defined fetchUserSubscription
        };
        const checkSubscriptionStatus = async () => {
           console.log('checkSubscriptionStatus needs implementation');
           const { user } = useAuthStore.getState();
           if (user) {
               await fetchUserSubscription(user.id); // Call the defined fetchUserSubscription
           }
        };
        const handleSubscriptionWebhook = async (event: any) => {
           console.log('handleSubscriptionWebhook needs implementation', event);
           const { user } = useAuthStore.getState();
           if (user) {
              await fetchUserSubscription(user.id); // Call the defined fetchUserSubscription
           }
        };
        const checkPaymentReminders = async () => {
             console.log('checkPaymentReminders needs implementation');
        };
        const getStripeSession = async (priceId: string, discountPercentage?: number): Promise<string> => {
             console.log('getStripeSession needs implementation', priceId, discountPercentage);
             toast.error("Funkcja płatności nie jest jeszcze zaimplementowana.");
             return '';
        };
        const handleCheckoutSuccess = async (sessionId: string) => {
             console.log('handleCheckoutSuccess needs implementation', sessionId);
              const { user } = useAuthStore.getState();
              if (user) {
                  toast.success("Płatność przetworzona! Aktualizowanie statusu subskrypcji...");
                  await fetchUserSubscription(user.id); // Call the defined fetchUserSubscription
               }
        };
        const createCustomerPortal = async (userId: string): Promise<string> => {
             console.log('createCustomerPortal needs implementation', userId);
              toast.error("Portal klienta nie jest jeszcze zaimplementowany.");
             return '';
        };
        const cancelSubscription = async (userId: string) => {
              console.log('cancelSubscription needs implementation', userId);
              toast("Anulowanie subskrypcji - funkcja w trakcie implementacji.");
         };

      // Return the full state object including all properties and methods
      return {
        // --- Initial State Properties ---
        plan: savedPlan || 'free',
        status: savedStatus || null,
        subscriptionId: localStorage.getItem('subscription_id'),
        quizGenerationsLeft: savedGenerationsLeft ? parseInt(savedGenerationsLeft, 10) : 1,
        currentBillingPeriod: {
          start: localStorage.getItem('billing_period_start'),
          end: localStorage.getItem('billing_period_end')
        },
        resetsLeft: 5,
        stripeCustomerId: null,
        stripeSubscriptionId: null,
        loading: false,
        error: null,
        initialized: false,
        discount: 0,
        analysisGenerated: 0,

        // --- State Modifying Methods ---
        setPlan,
        setSubscriptionId,
        setQuizGenerationsLeft,
        setLoading,
        setError,
        setInitialized,
        setStatus,
        setBillingPeriod,
        updateStripeDetails,
        setAnalysisGenerated,
        decrementQuizGenerations,
        resetQuizGenerations,
        decrementResets,
        resetMonthlyLimits,

        // --- Core Async Logic & Placeholders ---
        fetchUserSubscription,
        fetchSubscriptionStatus,
        checkSubscriptionStatus,
        handleSubscriptionWebhook,
        checkPaymentReminders,
        getStripeSession,
        handleCheckoutSuccess,
        createCustomerPortal,
        cancelSubscription,
      };
    },
    {
      name: 'subscription-storage',
      partialize: (state) => ({ 
        // Choose what state to persist
        plan: state.plan,
        status: state.status,
        subscriptionId: state.subscriptionId,
        quizGenerationsLeft: state.quizGenerationsLeft,
        currentBillingPeriod: state.currentBillingPeriod,
        // Persist discount and analysisGenerated if needed
        // discount: state.discount,
        // analysisGenerated: state.analysisGenerated,
      }),
    }
  )
);

export const { setAnalysisGenerated } = useSubscriptionStore.getState();
